import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CameraOutlined, DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { Button, Dropdown, Image, Input, Modal, Space, Table, message } from 'antd';
import columnKeys from '../constants/columns.js';
import Spinner from 'react-bootstrap/Spinner';
import "./ViewData.css";
import API_BASE from "../config/api";

const MAX_MEDIA_MB = 50;
const isVideoUrl = url => /\.(mp4|webm|mov)(\?|#|$)/i.test(url || '');

const ViewData = () => {
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [savingMedia, setSavingMedia] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null); // { url, title } of the video shown in the popup
  const [cameraIds, setCameraIds] = useState(null); // item ids the camera photo will be applied to (null = camera closed)
  const [cameraError, setCameraError] = useState('');
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null); // phone-camera fallback (<input capture>)
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const targetIdsRef = useRef([]); // items the next picked file will be applied to
  const [loadingMessage, setLoadingMessage] = useState('Loading, please wait...');
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const searchInput = useRef(null);

  useEffect(() => {
    fetchImports();
  }, []);

const fetchImports = async () => {
  setLoadingMessage('Loading, please wait...');

  try {
    const customerId = localStorage.getItem("customer_id");

    const response = await fetch(`${API_BASE}/getImports`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ customerId })
    });

    const imports = await response.json();

    if (!response.ok) {
      throw new Error(imports.error || "Failed to fetch data");
    }

    // ✅ Check if imports is an array
    if (!Array.isArray(imports)) {
      throw new Error(imports.error || "Unexpected response format");
    }

    const formattedData = imports.map((item, index) => ({
      ...item,
      key: index.toString(),
    }));

    setData(formattedData);
    setLoadingMessage("");

  } catch (error) {
    setLoadingMessage(error.toString());
  }
};



  // const fetchImports = async () => {
  //   setLoadingMessage('Loading, please wait...');
  //   try {
  //     const response = await fetch('https://ezwinds.com/api/getImports');
  //     const imports = await response.json();

  //     if (!response.ok) throw new Error(imports.error || 'Failed to fetch data');

  //     const formattedData = imports.map((item, index) => ({
  //       ...item,
  //       key: index.toString(),
  //     }));
  //     setData(formattedData);
  //     setLoadingMessage('');
  //   } catch (error) {
  //     setLoadingMessage(error.toString());
  //   }
  // };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
          <Input
              ref={searchInput}
              placeholder={`Search ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
              style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
              Reset
            </Button>
            <Button
                type="link"
                size="small"
                onClick={() => {
                  confirm({ closeDropdown: false });
                  setSearchText(selectedKeys[0]);
                  setSearchedColumn(dataIndex);
                }}
            >
              Filter
            </Button>
            <Button
                type="link"
                size="small"
                onClick={() => {
                  close();
                }}
            >
              Close
            </Button>
          </Space>
        </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: text => searchedColumn === dataIndex ? (
        <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
        />
    ) : (
        text
    ),
  });

  // ---- Item media (image / video) -> catalogue_product.image_url ----
  const applyMediaUrl = (ids, url) => {
    const idSet = new Set(ids.map(String));
    setData(prev => prev.map(item => (idSet.has(String(item.id)) ? { ...item, image_url: url } : item)));
  };

  const openPicker = ids => {
    targetIdsRef.current = ids;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFilePicked = e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow picking the same file again
    uploadFile(file, targetIdsRef.current);
  };

  const uploadFile = async (file, ids) => {
    if (!file || !ids || !ids.length) return;
    if (!/^(image|video)\//.test(file.type)) return message.error('Please choose an image or a video file.');
    if (file.size > MAX_MEDIA_MB * 1024 * 1024) return message.error(`File must be ${MAX_MEDIA_MB} MB or smaller.`);
    if (file.type.startsWith('image/') && file.size > 5 * 1024 * 1024) return message.error('Images must be 5 MB or smaller.');

    const form = new FormData();
    form.append('customer_id', localStorage.getItem('customer_id') || '');
    form.append('item_ids', JSON.stringify(ids));
    form.append('media', file);
    setSavingMedia(true);
    try {
      const res = await fetch(`${API_BASE}/inventory/media`, { method: 'POST', body: form });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      applyMediaUrl(ids, json.image_url);
      message.success(`Added to ${json.updated} item${json.updated === 1 ? '' : 's'}.`);
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setSavingMedia(false);
    }
  };

  const removeMedia = async ids => {
    setSavingMedia(true);
    try {
      const res = await fetch(`${API_BASE}/inventory/media`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: localStorage.getItem('customer_id') || '', item_ids: ids }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Could not remove');
      applyMediaUrl(ids, null);
      message.success('Media removed.');
    } catch (err) {
      message.error(err.message || 'Could not remove');
    } finally {
      setSavingMedia(false);
    }
  };

  // ---- Camera: live preview in a popup, capture -> upload ----
  const stopCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const startCamera = ids => {
    targetIdsRef.current = ids;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // No live camera API (e.g. plain http): phones still open their camera app via <input capture>.
      if (captureInputRef.current) captureInputRef.current.click();
      return;
    }
    setCameraError('');
    setCameraIds(ids);
  };

  useEffect(() => {
    if (!cameraIds) return undefined;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then(stream => {
        if (cancelled) return stream.getTracks().forEach(t => t.stop());
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setCameraError('Could not open the camera. Allow camera access in the browser, or use "Upload file" instead.'));
    return () => { cancelled = true; stopCamera(); };
  }, [cameraIds]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return message.error('The camera is not ready yet.');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const ids = cameraIds;
    canvas.toBlob(blob => {
      if (!blob) return message.error('Could not capture the photo.');
      setCameraIds(null);
      uploadFile(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }), ids);
    }, 'image/jpeg', 0.9);
  };

  const chooseSource = (ids, key) => (key === 'camera' ? startCamera(ids) : openPicker(ids));
  const sourceMenu = ids => ({
    items: [
      { key: 'camera', icon: <CameraOutlined />, label: 'Take photo' },
      { key: 'upload', icon: <UploadOutlined />, label: 'Upload image / video' },
    ],
    onClick: ({ key }) => chooseSource(ids, key),
  });

  // ---- Delete items (permanent) ----
  const deleteItems = ids => {
    Modal.confirm({
      title: ids.length === 1 ? 'Delete this item?' : `Delete ${ids.length} items?`,
      content: 'This permanently removes the item from your inventory and cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await fetch(`${API_BASE}/inventory/items`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customer_id: localStorage.getItem('customer_id') || '', item_ids: ids }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(json.error || 'Could not delete');
          const gone = new Set(ids.map(String));
          setData(prev => prev.filter(item => !gone.has(String(item.id))));
          setSelectedRowKeys(prev => prev.filter(k => !gone.has(String(k))));
          message.success(`Deleted ${json.deleted} item${json.deleted === 1 ? '' : 's'}.`);
        } catch (err) {
          message.error(err.message || 'Could not delete');
          throw err; // keep the dialog open
        }
      },
    });
  };

  // Global search: matches item title, UPC (barcode) and description
  const filteredData = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter(item =>
      ['title', 'upc', 'description', 'ean', 'major_category'].some(field =>
        (item[field] ?? '').toString().toLowerCase().includes(q)
      )
    );
  }, [data, globalSearch]);

  const columns = [
    {
      title: 'Media',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 130,
      render: (url, record) => (
        <Space direction="vertical" size={4} align="start">
          {url && (isVideoUrl(url)
            ? <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setVideoPreview({ url, title: record.title })}>
                <video src={url} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} muted preload="metadata" />
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, textShadow: '0 0 4px #000' }}>▶</span>
              </div>
            : <Image
                src={url}
                alt={record.title || 'Item'}
                width={64}
                height={64}
                style={{ objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                preview={{ mask: 'View' }}
              />)}
          <Space size={4}>
            <Dropdown menu={sourceMenu([record.id])} trigger={['click']} disabled={savingMedia}>
              <Button size="small" disabled={savingMedia}>{url ? 'Change ▾' : 'Add ▾'}</Button>
            </Dropdown>
            {url && (
              <Button size="small" danger type="link" disabled={savingMedia} onClick={() => removeMedia([record.id])}>
                Remove
              </Button>
            )}
          </Space>
        </Space>
      ),
    },
    ...columnKeys.map(column => ({
      title: column.displayName,
      dataIndex: column.dbName,
      key: column.dbName,
      ...getColumnSearchProps(column.dbName),
    })),
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteItems([record.id])}>
          Delete
        </Button>
      ),
    },
  ];

  return (
      <div >
        <h2>Imports</h2>
        {/*{loadingMessage*/}
        {/*    || <Table columns={columns} dataSource={data} />}*/}
        {loadingMessage ? (
            <>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <div>{ <div>{loadingMessage}</div>}</div>
            </>
        ) : (
            <>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search by title, UPC / barcode, description or category"
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                style={{ maxWidth: 420, marginBottom: 12 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                style={{ display: 'none' }}
                onChange={handleFilePicked}
              />
              <input
                ref={captureInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFilePicked}
              />
              {selectedRowKeys.length > 0 && (
                <Space wrap style={{ marginBottom: 12, display: 'flex' }}>
                  <strong>{selectedRowKeys.length} selected</strong>
                  <Dropdown menu={sourceMenu(selectedRowKeys)} trigger={['click']} disabled={savingMedia}>
                    <Button type="primary" loading={savingMedia}>Add image / video ▾</Button>
                  </Dropdown>
                  <Button danger disabled={savingMedia} onClick={() => removeMedia(selectedRowKeys)}>
                    Remove media
                  </Button>
                  <Button danger type="primary" icon={<DeleteOutlined />} disabled={savingMedia} onClick={() => deleteItems(selectedRowKeys)}>
                    Delete selected
                  </Button>
                  <Button type="link" onClick={() => setSelectedRowKeys([])}>Clear selection</Button>
                </Space>
              )}
              <Modal
                open={!!cameraIds}
                title="Take a photo"
                okText="Capture"
                cancelText="Cancel"
                destroyOnClose
                onOk={capturePhoto}
                okButtonProps={{ disabled: !!cameraError }}
                onCancel={() => setCameraIds(null)}
              >
                {cameraError
                  ? <div style={{ color: '#cf1322' }}>{cameraError}</div>
                  : <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: 6, background: '#000' }} />}
              </Modal>
              <Modal
                open={!!videoPreview}
                title={videoPreview?.title || 'Video'}
                footer={null}
                destroyOnClose
                width={720}
                onCancel={() => setVideoPreview(null)}
              >
                {videoPreview && <video src={videoPreview.url} controls autoPlay style={{ width: '100%', borderRadius: 6 }} />}
              </Modal>
              <Table
                className="custom-table"
                rowKey="id"
                columns={columns}
                dataSource={filteredData}
                rowSelection={{ selectedRowKeys, onChange: keys => setSelectedRowKeys(keys) }}
                scroll={{ x: 'max-content' }}
              />
            </>
        )}
      </div>
  );
};

export default ViewData;
